import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import Button from '../../../components/buttons/Button';
import { useEffect, useRef } from 'react';
import FormUpload from '../../../components/formInputs/FormUpload';
import { getMedia, scrollToError } from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IExteriorInteriorColorForm } from '../types/interfaces';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import { customStyles } from '../../../utils/SelectStyles';
import SwitchActivation from '../../../components/layout/SwitchActivation';
import useModelSelectOptions from '../../../hooks/useModelSelectOptions';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';
import { makesSelectProps } from '../../../utils/selectOptionsProps';

const CreateExteriorInteriorColorsForm = ({ interior }: { interior?: boolean }) => {
  const { id } = useParams();
  const formRef = useRef(null);
  const { t, i18n } = useTranslation();

  const type = interior ? 'interior' : 'exterior';

  const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    queryKey: [`${type}-colors`, id],
    invalidateQueryKey: [`${type}-colors`],
    id,
    singleGetApi: `/admin/${type}-colors/${id}`,
    addApi: `/admin/${type}-colors`,
    editApi: `/admin/${type}-colors/${id}`,
    itemName: t(`${type}_color`),
    listRoute: `/${type}-colors`
  });

  const { modelOptions: vehicleMakesOptions } = useModelSelectOptions(makesSelectProps());

  const validationSchema = Yup.object({
    display_name: Yup.object().shape({
      ar: Yup.string().required(t('required')).min(3, t('name_short')),
      en: Yup.string().required(t('required')).min(3, t('name_short'))
    })
  });

  const initialValues: IExteriorInteriorColorForm = {
    ...apiData,

    display_name: {
      en: apiData?.display_name?.en || '',
      ar: apiData?.display_name?.ar || ''
    },

    code: apiData?.code || '',
    has_addon: apiData?.has_addon !== undefined ? apiData.has_addon : 0,

    circle_image: getMedia(apiData?.media, 'circle_image')?.url || '',
    vehicle_make_id: apiData?.vehicle_make?.id || (vehicleMakesOptions?.length == 1 ? vehicleMakesOptions[0]?.value : '')
  };

  if (getDataLoading)
    return (
      <FormDynamicSkeleton
        sections={[
          [{ columns: 4, items: 1, type: 'textarea' }],
          [
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 1, type: 'switch' }
          ]
        ]}
        buttons={1}
      />
    );

  return (
    <div className={'form_section'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setErrors }) => {
          let value = { ...values, _method: 'POST' };
          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          handleSubmit({ ...value }, setErrors);
        }}
      >
        {(formik) => (
          <>
            <Form>
              <SectionHeader title={t('icon')}>
                <div className="inputs_grid with_bottom_padding">
                  <FieldWrapper gridSize={3}>
                    <FormUpload errors={formik?.errors.circle_image as string} touched={formik?.touched?.circle_image as boolean} formik={formik} name={`circle_image`} />
                  </FieldWrapper>
                </div>
              </SectionHeader>
              <SectionHeader title={t(`${type}_color_details`)} customStyle="last">
                <div className="inputs_grid with_bottom_padding">
                  <FieldWrapper title={t('code')} inputName={`code`} inputPlaceholder={t('code')} input customPadding gridSize={6} />
                  <FieldWrapper title={t('name')} inputName={`name`} inputPlaceholder={t('name')} input customPadding gridSize={6} />
                  <FieldWrapper title={t('display_name_en')} inputName={`display_name.en`} inputPlaceholder={t('display_name_en')} input customPadding gridSize={6} />
                  <FieldWrapper title={t('display_name_ar')} inputName={`display_name.ar`} inputPlaceholder={t('display_name_ar')} input customPadding gridSize={6} />
                  <FieldWrapper title={t('hex_code')} inputName={`hexcode`} inputPlaceholder={t('hex_code')} input customPadding gridSize={6} />
                  {!interior && vehicleMakesOptions?.length > 1 && (
                    <FieldWrapper
                      customPadding
                      title={t('vehicle_make')}
                      inputPlaceholder={t('vehicle_make')}
                      inputName={`vehicle_make_id`}
                      options={vehicleMakesOptions}
                      selectStyle={customStyles}
                      select
                      gridSize={6}
                    />
                  )}
                </div>
                {!interior && (
                  <div className="switch_container">
                    <SwitchActivation onChange={(value: any) => formik.setFieldValue('has_addon', value?.target?.checked ? 1 : 0)} active={formik.values.has_addon} />
                    <p>
                      {t('has_addon_price')} <span className="side_note">({t('active_means_enabled')})</span>
                    </p>
                  </div>
                )}
              </SectionHeader>
              <div className="form_button reverse">
                <Button
                  loading={submitLoading}
                  onClick={() => {
                    scrollToError(!formik.isValid, formRef);
                  }}
                >
                  <span className="bold">{t('save')}</span>
                </Button>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateExteriorInteriorColorsForm;
